import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  QueryList,
  SimpleChanges,
  TrackByFunction,
  ViewChild,
} from '@angular/core';

import { merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { provideAnalyticsViewContextFromRouterData } from '@recruitee/analytics';
import { trackByProp, trackByValue } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  IconComponent,
  IconColorDirective,
  ButtonComponent,
  RtTooltip,
  IconHelpWithDropdownComponent,
} from '@recruitee/user-interface';

import { PlaceholdersAnalyticsService } from '../../../analytics/placeholders.analytics';
import { InsertPlaceholderInputDirective } from '../insert-placeholder-input.directive';
import { InsertPlaceholderDirective } from '../insert-placeholder.directive';
import {
  Placeholder,
  PlaceholderCategory,
  PlaceholderConfigMap,
  PlaceholderId,
} from '../placeholder';
import { PlaceholderButtonComponent } from '../placeholder-button/placeholder-button.component';
import { PlaceholderCategoryButtonComponent } from '../placeholder-category-button/placeholder-category-button.component';
import { PlaceholderCategorySelectDropdownComponent } from '../placeholder-category-select-dropdown/placeholder-category-select-dropdown.component';
import { PlaceholderService } from '../placeholder.service';

@Component({
  selector: 'rt-placeholders-list',
  templateUrl: './placeholders-list.component.html',
  styleUrls: ['./placeholders-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PlaceholdersAnalyticsService],
  viewProviders: [...provideAnalyticsViewContextFromRouterData()],
  standalone: true,
  imports: [
    IconHelpWithDropdownComponent,
    PlaceholderCategoryButtonComponent,
    PlaceholderButtonComponent,
    InsertPlaceholderDirective,
    RtTooltip,
    ButtonComponent,
    OverlayOriginDirective,
    IconColorDirective,
    IconComponent,
    ConnectedOverlayDirective,
    PlaceholderCategorySelectDropdownComponent,
    TranslatePipe,
  ],
})
export class PlaceholdersListComponent {
  @Input() public placeholders: Placeholder[];

  @Input() public set target(target: InsertPlaceholderInputDirective) {
    this.targetsClear$.next();

    if (target instanceof InsertPlaceholderInputDirective) {
      this.targets$.next([target]);
    }
  }

  @Input() public set targets(targets: QueryList<InsertPlaceholderInputDirective>) {
    this.targetsClear$.next(); // always destroy existing subscriptions

    if (targets instanceof QueryList) {
      this.targets$.next(targets.toArray());

      targets.changes
        .pipe(
          takeUntil(merge(this.ngOnDestroy$.asObservable(), this.targetsClear$.asObservable())),
          map(_ => targets.toArray()),
        )
        .subscribe(this.targets$);
    } else {
      this.targets$.next([]);
    }
  }
  @ViewChild('rtPlaceholders') public placeholdersEl: any;
  @ViewChild('moreDropdown') public moreDropdown: ConnectedOverlayDirective;

  public activeTarget: InsertPlaceholderInputDirective | undefined;
  public activeTargetConfig: PlaceholderConfigMap = {};
  public filteredPlaceholders: Placeholder[] = [];
  public noCategoryPlaceholders: Placeholder[] = [];
  public noCategoryPlaceholdersCollection = [
    PlaceholderId.JobOffer,
    PlaceholderId.JobOfferAll,
    PlaceholderId.Company,
  ];
  public categories = [PlaceholderCategory.Candidate];

  public hasMore: boolean = false;
  public moreNoCategoryPlaceholdersCollection = [PlaceholderId.Department];
  public moreNoCategoryPlaceholders: Placeholder[] = [];
  public moreCategoriesCollection = [
    PlaceholderCategory.EmailSender,
    PlaceholderCategory.Event,
    PlaceholderCategory.HiringManager,
    PlaceholderCategory.Recruiter,
  ];
  public moreCategories: PlaceholderCategory[] = [];

  public trackByCategoryName = trackByValue;
  public trackByPlaceholderName: TrackByFunction<Placeholder> = trackByProp<Placeholder>({
    prop: 'id',
  });

  private ngOnDestroy$ = new Subject<void>();
  private targetsClear$ = new Subject<void>();
  private targets$: ReplaySubject<InsertPlaceholderInputDirective[]> = new ReplaySubject(1);
  private activeTarget$: Observable<InsertPlaceholderInputDirective | undefined> =
    this.createActiveTargetStream();
  constructor(
    private cdRef: ChangeDetectorRef,
    private placeholderService: PlaceholderService,
    private placeholderAnalyticsService: PlaceholdersAnalyticsService,
  ) {}

  public ngOnInit(): void {
    this.activeTarget$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(target => {
      this.activeTarget = target;
      this.activeTargetConfig = target?.placeholdersConfig || {};
      this.cdRef.detectChanges();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.placeholders) {
      this.filteredPlaceholders = changes.placeholders.currentValue.filter(
        ({ id }: { id: PlaceholderId }) =>
          !this.placeholderService.deprecatedCollection.includes(id),
      );
      this.noCategoryPlaceholders = this.noCategoryPlaceholdersCollection.flatMap(placeholderId =>
        this.placeholderService.filterPlaceholders(this.filteredPlaceholders, placeholderId),
      );

      this.moreNoCategoryPlaceholders = this.moreNoCategoryPlaceholdersCollection.flatMap(
        placeholderId =>
          this.placeholderService.filterPlaceholders(this.filteredPlaceholders, placeholderId),
      );

      this.moreCategories = this.moreCategoriesCollection.filter(category =>
        this.placeholderService.isCategoryVisible(this.filteredPlaceholders, category),
      );

      this.hasMore =
        this.moreNoCategoryPlaceholders.length !== 0 || this.moreCategories.length !== 0;
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackInsertPlaceholder(placeholderName: string) {
    this.placeholderAnalyticsService.insertPlaceholder({ placeholderName });
  }

  public moreDropdownToggle(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.moreDropdown.open = !this.moreDropdown.open;
  }

  private createActiveTargetStream(): Observable<InsertPlaceholderInputDirective | undefined> {
    return this.targets$.pipe(
      takeUntil(this.ngOnDestroy$.asObservable()),
      switchMap(targets =>
        merge(
          ...targets.map(target =>
            merge(
              target.input.focus.pipe(map(() => target)),
              target.input.blur.pipe(map(() => undefined)),
            ),
          ),
        ),
      ),
    );
  }
}
